/**
 * Mapas de etiquetas localizadas para los valores de enum de la política LLM.
 *
 * Cada mapa traduce el valor técnico (el que viaja en el payload y se conserva
 * en el `value` del `<option>`) a la clave i18n `policy.*` que lo etiqueta.
 * Los selects de las páginas DR/tenant y el panel de «Política efectiva»
 * comparten estos mapas para que un mismo valor se muestre siempre igual.
 */

import type {
  AsyncServiceTier,
  AsyncTierOnUnavailable,
  BudgetAwareProfile,
  ByopPolicy,
  DashboardWindow,
  FrontierOnUnavailable,
  GroundingCheck,
  HealthThreshold,
  OutputEffort,
  PlanReview,
  PlanningOnFailure,
  PolicyStrategy,
  PromptCachePartition,
  PromptCacheTtl,
  RoutingMode,
  RoutingOnFailure,
  ToolExposure,
} from '../types/policy';

export const STRATEGY_LABELS: Record<PolicyStrategy, string> = {
  priority: 'policy.enumStrategyPriority',
  cost: 'policy.enumStrategyCost',
  latency: 'policy.enumStrategyLatency',
  round_robin: 'policy.enumStrategyRoundRobin',
  weighted: 'policy.enumStrategyWeighted',
  sticky_tenant: 'policy.enumStrategyStickyTenant',
};

export const BYOP_LABELS: Record<ByopPolicy, string> = {
  prefer_platform: 'policy.enumByopPreferPlatform',
  prefer_byop: 'policy.enumByopPreferByop',
  only_byop: 'policy.enumByopOnlyByop',
  only_platform: 'policy.enumByopOnlyPlatform',
};

export const HEALTH_LABELS: Record<HealthThreshold, string> = {
  skip_degraded: 'policy.healthSkipDegraded',
  allow_degraded: 'policy.healthAllowDegraded',
};

export const ROUTING_MODE_LABELS: Record<RoutingMode, string> = {
  auto: 'policy.enumRoutingModeAuto',
  direct: 'policy.enumRoutingModeDirect',
  planned: 'policy.enumRoutingModePlanned',
};

export const ROUTING_FAILURE_LABELS: Record<RoutingOnFailure, string> = {
  degrade_direct: 'policy.enumRoutingFailureDegradeDirect',
  fail_closed: 'policy.enumFailClosed',
  skip_stage: 'policy.enumSkipStage',
  preserve_mode: 'policy.enumRoutingFailurePreserveMode',
};

export const PLANNING_FAILURE_LABELS: Record<PlanningOnFailure, string> = {
  skip: 'policy.enumSkip',
  fail_closed: 'policy.enumFailClosed',
};

export const GROUNDING_LABELS: Record<GroundingCheck, string> = {
  off: 'policy.enumOff',
  warn: 'policy.enumGroundingWarn',
  enforce: 'policy.enumGroundingEnforce',
};

export const FRONTIER_UNAVAILABLE_LABELS: Record<
  FrontierOnUnavailable,
  string
> = {
  standard: 'policy.enumStandard',
  fail_closed: 'policy.enumFailClosed',
};

export const PLAN_REVIEW_LABELS: Record<PlanReview, string> = {
  off: 'policy.enumOff',
  high_risk: 'policy.enumHighRisk',
  always: 'policy.enumAlways',
};

export const DASHBOARD_WINDOW_LABELS: Record<DashboardWindow, string> = {
  '24h': 'policy.window24h',
  '7d': 'policy.window7d',
  '30d': 'policy.window30d',
};

/** TTLs con etiqueta propia; `5m`/`1h` se muestran tal cual (unidades). */
export const CACHE_TTL_LABELS: Partial<Record<PromptCacheTtl, string>> = {
  auto: 'policy.enumCacheTtlAuto',
};

export const CACHE_PARTITION_LABELS: Record<PromptCachePartition, string> = {
  tenant: 'policy.enumPartitionTenant',
  tenant_agent: 'policy.enumPartitionTenantAgent',
};

export const TOOL_EXPOSURE_LABELS: Record<ToolExposure, string> = {
  stable: 'policy.enumToolExposureStable',
  sticky_working_set: 'policy.enumToolExposureStickyWorkingSet',
  deferred: 'policy.enumToolExposureDeferred',
};

export const OUTPUT_EFFORT_LABELS: Record<OutputEffort, string> = {
  provider_default: 'policy.enumOutputEffortProviderDefault',
  low: 'policy.enumOutputEffortLow',
  medium: 'policy.enumOutputEffortMedium',
  high: 'policy.enumOutputEffortHigh',
};

export const ASYNC_SERVICE_TIER_LABELS: Record<AsyncServiceTier, string> = {
  standard: 'policy.enumStandard',
  flex: 'policy.enumFlex',
  batch: 'policy.enumBatch',
};

export const ASYNC_TIER_UNAVAILABLE_LABELS: Record<
  AsyncTierOnUnavailable,
  string
> = {
  retry_standard: 'policy.enumRetryStandard',
  wait_retry: 'policy.enumWaitRetry',
};

export const BUDGET_PROFILE_LABELS: Record<BudgetAwareProfile, string> = {
  off: 'policy.enumOff',
  notify: 'policy.enumBudgetNotify',
  tighten: 'policy.enumBudgetTighten',
};

/** Traduce un mapa valor→clave i18n con el `t` activo. */
export function translateLabels(
  translate: (key: string) => string,
  labels: Record<string, string>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(labels).map(([value, key]) => [value, translate(key)]),
  );
}
